
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { isAppInstalled, resetInstallationCache } from '@/utils/notificationsManager';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

declare global {
  interface WindowEventMap {
    'beforeinstallprompt': BeforeInstallPromptEvent;
    'appinstalled': Event;
  }
}

// Global state for the deferred prompt - shared across all components
let globalDeferredPrompt: BeforeInstallPromptEvent | null = null;
let isListenerRegistered = false;

export const usePwaInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(globalDeferredPrompt);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const [appInstalled, setAppInstalled] = useState(false);
  const initialCheckDone = useRef(false);
  const installStatusRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!initialCheckDone.current) {
      console.log("usePwaInstall hook inicializado");
      initialCheckDone.current = true;
      
      // Check if app is installed once and cache the result
      const installedStatus = isAppInstalled();
      installStatusRef.current = installedStatus;
      setAppInstalled(installedStatus);

      // Check if the device is iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
      setIsIOSDevice(isIOS);
      console.log("¿Es dispositivo iOS?:", isIOS);

      // Set up global event listeners only once
      if (!isListenerRegistered) {
        isListenerRegistered = true;
        
        // Function to handle the beforeinstallprompt event
        const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
          e.preventDefault();
          console.log("Evento beforeinstallprompt capturado globalmente");
          globalDeferredPrompt = e;
          setDeferredPrompt(e);
          setIsInstallable(true);
        };

        // Function to handle app installation
        const handleAppInstalled = () => {
          console.log("Evento appinstalled recibido globalmente");
          globalDeferredPrompt = null;
          setDeferredPrompt(null);
          setIsInstallable(false);
          setAppInstalled(true);
          installStatusRef.current = true;
          resetInstallationCache();
          toast.success('¡App instalada correctamente!');
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);
        console.log("EventListeners globales de PWA registrados");

        // Register service worker if supported
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistration().then(reg => {
            if (!reg) {
              navigator.serviceWorker.register('/sw.js')
                .then(registration => {
                  console.log('ServiceWorker registered:', registration.scope);
                })
                .catch(error => {
                  console.error('Error registering ServiceWorker:', error);
                });
            }
          });
        }
      } else {
        // If listeners are already registered, just sync the current state
        setDeferredPrompt(globalDeferredPrompt);
        setIsInstallable(!!globalDeferredPrompt);
      }
    }
  }, []);

  const handleInstallClick = async () => {
    console.log("Botón de instalación clickeado, deferredPrompt:", deferredPrompt ? "disponible" : "no disponible");
    
    if (!deferredPrompt) {
      try {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
          console.log("Navegador compatible con PWA pero no se capturó el prompt");
          toast.info('Para instalar, usa la opción "Añadir a pantalla de inicio" en el menú del navegador');
          return;
        }
        
        toast.error('No se puede instalar en este dispositivo. El navegador no es compatible.');
        console.log("Navegador no compatible con instalación de PWA");
      } catch (error) {
        console.error('Error al intentar la instalación manual:', error);
      }
      return;
    }

    try {
      console.log("Mostrando prompt de instalación...");
      await deferredPrompt.prompt();
      
      console.log("Prompt mostrado, esperando respuesta del usuario...");
      const choiceResult = await deferredPrompt.userChoice;
      console.log("Respuesta del usuario recibida:", choiceResult.outcome);
      
      if (choiceResult.outcome === 'accepted') {
        console.log("¡Usuario aceptó instalar la app!");
        toast.success('¡La app se está instalando!');
        setIsInstallable(false);
      } else {
        console.log("Usuario canceló la instalación");
        toast.info('Instalación cancelada. Puedes instalar la app más tarde.');
      }
      
      // Clear the saved event globally
      globalDeferredPrompt = null;
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Error durante la instalación:', error);
      toast.error('Hubo un error al instalar la app. Intenta nuevamente.');
    }
  };

  // Use cached installation status to avoid repeated calls
  const appInstalledValue = installStatusRef.current !== null ? installStatusRef.current : appInstalled;

  return {
    deferredPrompt,
    isInstallable,
    isIOSDevice,
    appInstalled: appInstalledValue,
    handleInstallClick
  };
};
